import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, interval, Observable, Subject, Subscription } from 'rxjs';
import { takeUntil, filter, take } from 'rxjs/operators';
import { DialogService } from '../dialog/dialog.service';
import { AuthService } from '../auth/auth.service';
import { AppConstants } from 'src/app/constants/app-constants';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';

/**
 * Session service handles timing related to user sessions. This
 * service does not handle the actual authentication of the user,
 * that is done in the UserService
 */
@Injectable({
  providedIn: 'root'
})
export class SessionService implements OnDestroy {
  // CONSTANTS

  /** Flag to enable or disable debug mode for logging */
  private readonly DEBUG_MODE: boolean = !environment.production;

  /** How long the service should ignore refresh requests after refreshing */
  private readonly SESSION_REFRESH_LIMIT = 1000 * 60 * 1; // 1 minute

  /** Time remaining before session extend dialog opens */
  private readonly SESSION_TIMEOUT_LIMIT = 1000 * 60 * 1; // 1 minute

  /** Offset between actual JWT expiry and time remaining counter, to account for network latency */
  private readonly SESSION_TIMEOUT_OFFSET = 1000 * 30; // 30 seconds


  // STATE PROPERTIES

  private _timeLeftInSession: number = 0;      // Time left in session
  private _lastRefreshedTimer: number = 0;     // Time tracker for refresh rate limiting
  private _isRefreshingToken: boolean = false; // Flag to track if token refresh is in progress
  private _dialogOpen: boolean = false;        // Whether or not the dialog is open already

  // OBSERVABLES

  private readonly _timer$: Observable<number> = interval(1000); // Timer observable for 1 second intervals
  private readonly _destroy$ = new Subject<void>();              // Manage subscription cleanup
  private readonly _stopClock$ = new Subject<void>();            // Stop clock timer subscriptions

  /** Public observable for clock display */
  private readonly _clock = new BehaviorSubject<string>("0:00");
  public readonly clock$ = this._clock.asObservable();

  // LIFECYCLE METHODS

  /**
   * Default constructor for the session service which initializes timers
   */
  constructor(
    private _authService: AuthService,
    private _dialogService: DialogService,
    private _router: Router,
  ) {
    this._initializeAuthStatusListener();
    this._initializeSessionTimerListener();
    this._initializeVisibilityChangeListener();
  }

  /**
   * Clean up subscriptions when service is destroyed
   */
  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
    this._stopClock$.complete();
    this._clock.complete();
  }

  // METHODS

  /**
   * Refresh the session timer by obtaining a new token and resetting the clock
   */
  public refresh(): void {
    // Skip if the refresh timer is below the limit or if the dialog is open
    if (this._lastRefreshedTimer < this.SESSION_REFRESH_LIMIT || this._dialogOpen) {
      this._log("[SESSION]: Skipping refresh");
      return;
    }

    this._performTokenRefresh();
  }

  /**
   * Refresh no matter what the refresh timer is at
   */
  public hardRefresh(): void {
    this._performTokenRefresh();
  }

  /**
   * Get whether the session dialog is currently open
   */
  public get dialogOpen(): boolean {
    return this._dialogOpen;
  }

  // INITIALIZATION METHODS

  /**
   * Initialize listener for authentication status changes. This happens only once when the user first logs in
   */
  private _initializeAuthStatusListener(): void {
    this._authService.isAuthenticated$.pipe(
      filter(isAuthenticated => isAuthenticated), // Only proceed on authentication
    ).subscribe(isAuthenticated => {
      if (isAuthenticated) {
        this._log("[SESSION]: User authenticated, starting session clock");
        this._lastRefreshedTimer = 0;
        this._resetClock();
      }
    });
  }

  /**
   * Initialize the session timeout warning timer
   */
  private _initializeSessionTimerListener(): void {
    this._timer$.pipe(
      takeUntil(this._destroy$), // Cleanup on destroy
    ).subscribe(() => {
      // Check if we should open the dialog
      if (!this._shouldSkipDialogOpen()) {
        this._openExtendSessionDialog();
      }
    });
  }

  /**
   * Initialize listener for browser tab visibility changes
   */
  private _initializeVisibilityChangeListener(): void {
    // Listen for visibility change events
    document.addEventListener('visibilitychange', () => {
      // If the document is now visible, handle the change
      if (!document.hidden) this._handleVisibilityChange();
    });
  }

  // HELPER METHODS

  /**
   * Check if session timeout dialog should be skipped. This is true if one of the following is true:
   * - Document is hidden
   * - Extend session dialog is already open
   * - User is not authenticated
   * - Time left in session is above the timeout limit
   * - Token is currently being refreshed
   * - Time left in session is less than or equal to zero
   */
  private _shouldSkipDialogOpen(): boolean {
    return (
      document.hidden ||
      this._dialogOpen ||
      !this._authService.authenticated ||
      this._timeLeftInSession > this.SESSION_TIMEOUT_LIMIT ||
      this._isRefreshingToken ||
      this._timeLeftInSession <= 0
    );
  }

  /**
   * Open the extend session dialog
   */
  private _openExtendSessionDialog(): void {
    this._log("[SESSION]: Time left in session is below warning limit, opening extend session dialog");

    this._dialogOpen = true;
    this._dialogService.openExtendSession().pipe(
      takeUntil(this._destroy$), // Cleanup on destroy
    ).subscribe(() => {
      this._dialogOpen = false;
    });
  }

  /**
   * Handle when user switches back to the tab
   */
  private _handleVisibilityChange(): void {
    // Ignore if user is not authenticated
    if (!this._authService.authenticated) return;

    // Update time left in session
    const newTimeLeft = this._calculateExpiryTime(this._authService.token || "");
    this._lastRefreshedTimer += this._timeLeftInSession - newTimeLeft;
    this._timeLeftInSession = newTimeLeft;

    this._log("[SESSION]: Document is visible again, recalculating time left in session:", this._timeLeftInSession);

    // If the time left is below zero, log out the user
    if (this._timeLeftInSession <= 0) {
      this._log("[SESSION]: Session has expired while tab was hidden, logging out");
      this._logoutUser();
    }
  }

  /**
   * Perform token refresh operation
   */
  private _performTokenRefresh(): void {
    if (this._isRefreshingToken) return;
    this._isRefreshingToken = true;
    
    this._authService.refreshToken().pipe(
      takeUntil(this._destroy$), // Cleanup on destroy
    ).subscribe(() => {
      this._isRefreshingToken = false;
    });
  }

  /**
   * Log out the user and navigate to login page
   */
  private _logoutUser(): void {
    this._router.navigate([AppConstants.MENU_LOGIN]);
    this._authService.logout();
  }

  /**
   * Refresh the session clock
   */
  private _resetClock(): void {
    this._log("[SESSION]: Resetting clock");

    // Stop other timers
    this._stopClock$.next();

    // Get the user's token
    const token = this._authService.token;
    if (!token) return;

    this._timeLeftInSession = this._calculateExpiryTime(token);
    this._startClockCountdown();
  }

  /**
   * Start the countdown timer for the session clock
   */
  private _startClockCountdown(): void {
    // Calculate minutes and seconds
    const initial = this._timeLeftInSession / 1000; // Divide by milliseconds
    let minutes = Math.floor(initial / 60);
    let seconds = initial % 60;

    // Subscribe to clock changes
    this._timer$.pipe(
      takeUntil(this._stopClock$), // Continue until stop signal
      takeUntil(this._destroy$)    // Cleanup on destroy
    ).subscribe(() => {
      // Skip countdown if unauthenticated
      if (!this._authService.authenticated) return;

      this._lastRefreshedTimer += 1000;

      // If the clock is paused, do not decrement time left
      if (this._isRefreshingToken) {
        this._log("[SESSION]: Clock is paused, not decrementing time left");
        return;
      }

      // Decrement time left
      this._timeLeftInSession -= 1000;
      seconds = this._timeLeftInSession / 1000 % 60;
      minutes = Math.floor(this._timeLeftInSession / 1000 / 60);

      //this._log(`[SESSION]: Time left in session: ${minutes} minutes, ${seconds} seconds`);

      // Update timer
      this._clock.next(`${minutes}:${("" + seconds).length === 1 ? "0" : ""}${seconds}`);

      // If no time is left, log out
      if (this._timeLeftInSession <= 0) {
        this._logoutUser();
      }
    });
  }


  /**
   * Calculate expiry time based on the JWT token
   * @param token The JWT token to calculate the expiry time from
   */
  private _calculateExpiryTime(token: string): number {
    if (!token) {
      return 0;
    }

    // Calculate time left in session from the user's token
    try {
      const payload = token.split('.')[1];
      const decoded = JSON.parse(atob(payload));

      // Calculate time left in session and truncate to seconds
      const timeLeft = Math.floor((decoded.exp * 1000 - Date.now()) / 1000) * 1000 - this.SESSION_TIMEOUT_OFFSET;
      return timeLeft;
    } catch (error) {
      // Catch errors and result to default timeout interval
      throw new Error("Invalid token format");
    }
  }

  /**
   * Wrapper around console log
   */
  private _log(...messages: any[]): void {
    if (this.DEBUG_MODE) {
      console.log(...messages);
    }
  }
}